import type { AtvModel } from '../hooks/useAtvModels';
import type { Accessory } from '../hooks/useAccessories';
import {
  BusySlot, BusySnapshot, BookingSelection, BookingQuote, RentalConfig, AvailabilitySnapshot,
  ReservationPayload, ReservationCredentials, ReservationStatus, parseBusyTime,
} from './model';

export const BOOKING_API_BASE = (process.env.NEXT_PUBLIC_BOOKING_API_BASE
  || 'https://europe-west3-atv-rent.cloudfunctions.net').replace(/\/+$/, '');

interface ErrorDetails {
  reservationId?: string;
  statusToken?: string;
  currentPrice?: unknown;
  field?: string;
  retryable?: boolean;
}

export class BookingApiError extends Error {
  constructor(
    public readonly code: string, public readonly status?: number,
    public readonly details: ErrorDetails = {}, public readonly retryAfterSeconds = 0,
  ) {
    super(code === 'HTTP' ? `Booking API HTTP ${status}` : `Booking API ${code}`);
    this.name = 'BookingApiError';
  }
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new BookingApiError('INVALID_RESPONSE');
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) throw new BookingApiError('INVALID_RESPONSE');
  return value;
}

function amount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new BookingApiError('INVALID_RESPONSE');
  return value;
}

function list(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw new BookingApiError('INVALID_RESPONSE');
  return value;
}

let readLimitedUntil = 0;
let createLimitedUntil = 0;
async function read(endpoint: string, signal: AbortSignal, body?: unknown, key?: string): Promise<unknown> {
  const limitedUntil = endpoint === 'createReservation' ? createLimitedUntil
    : ['getRentalConfig', 'checkAvailability', 'getRentalQuote', 'getReservationStatus'].includes(endpoint) ? readLimitedUntil : 0;
  if (limitedUntil > Date.now()) {
    throw new BookingApiError('RATE_LIMITED', 429, {}, Math.ceil((limitedUntil - Date.now()) / 1000));
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) controller.abort();
  let timedOut = false;
  const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 12_000);
  try {
    const response = await fetch(`${BOOKING_API_BASE}/${endpoint}`, {
      signal: controller.signal, cache: 'no-store',
      ...(body === undefined ? {} : {
        method: 'POST', body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json', ...(key ? { 'Idempotency-Key': key } : {}) },
      }),
    });
    const raw = await response.text();
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      if (response.ok) throw new BookingApiError('INVALID_RESPONSE');
    }
    if (!response.ok) {
      const retryHeader = response.headers.get('Retry-After');
      const retry = retryHeader ? (/^\d+$/.test(retryHeader)
        ? Number(retryHeader) : Math.max(0, Math.ceil((Date.parse(retryHeader) - Date.now()) / 1000))) : 0;
      const root = data && typeof data === 'object' && !Array.isArray(data) ? object(data) : {};
      const nested = root.error && typeof root.error === 'object' && !Array.isArray(root.error) ? object(root.error) : {};
      const code = typeof nested.code === 'string' && /^[A-Z_]{1,64}$/.test(nested.code) ? nested.code : 'HTTP';
      const details: ErrorDetails = {
        ...(typeof nested.reservationId === 'string' ? { reservationId: nested.reservationId } : {}),
        ...(typeof nested.statusToken === 'string' ? { statusToken: nested.statusToken } : {}),
        ...(nested.currentPrice !== undefined ? { currentPrice: nested.currentPrice } : {}),
        ...(typeof nested.field === 'string' ? { field: nested.field } : {}),
        ...(typeof nested.retryable === 'boolean' ? { retryable: nested.retryable } : {}),
      };
      const wait = Number.isFinite(retry) && retry > 0 ? retry : response.status === 429 ? 30 : 0;
      if (response.status === 429) {
        if (endpoint === 'createReservation') createLimitedUntil = Date.now() + wait * 1000;
        else readLimitedUntil = Date.now() + wait * 1000;
      }
      throw new BookingApiError(code, response.status, details, wait);
    }
    return data;
  } catch (error) {
    if (timedOut) throw new BookingApiError('TIMEOUT');
    if (signal.aborted || error instanceof BookingApiError) throw error;
    throw new BookingApiError('NETWORK');
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
}

export function parseAtvs(value: unknown): AtvModel[] {
  const ids = new Set<string>();
  return list(object(value).atvs).flatMap(item => {
    const atv = object(item);
    if (typeof atv.enabled !== 'boolean') throw new BookingApiError('INVALID_RESPONSE');
    if (!atv.enabled) return [];
    const id = text(atv.id);
    if (ids.has(id)) throw new BookingApiError('INVALID_RESPONSE');
    ids.add(id);
    return [{
      id, name: text(atv.name), enabled: true,
      price: amount(atv.price), pricePerHour: amount(atv.pricePerHour),
      weekendPrice: amount(atv.weekendPrice), weekPrice: amount(atv.weekPrice),
      depositHour: amount(atv.depositHour), depositDay: amount(atv.depositDay),
      tag: typeof atv.tag === 'string' ? atv.tag : '',
    }];
  });
}

function busySlot(value: unknown): BusySlot {
  const entry = object(value);
  const slot = {
    fromDate: text(entry.fromDate), fromHour: text(entry.fromHour),
    toDate: text(entry.toDate), toHour: text(entry.toHour),
  };
  const start = parseBusyTime(slot.fromDate, slot.fromHour);
  const end = parseBusyTime(slot.toDate, slot.toHour);
  if (!start || !end || end.valueOf() <= start.valueOf()) throw new BookingApiError('INVALID_RESPONSE');
  return slot;
}

export function parseBusySnapshot(value: unknown): BusySnapshot {
  const data = object(value);
  const global = list(data.busySlots).map(busySlot);
  const byModel: Record<string, BusySlot[]> = Object.create(null);
  if (Array.isArray(data.busyByModel)) {
    for (const value of data.busyByModel) {
      const entry = object(value);
      const model = text(entry.model);
      (byModel[model] ||= []).push(busySlot(entry));
    }
  } else if (data.busyByModel !== undefined) {
    for (const [model, slots] of Object.entries(object(data.busyByModel))) {
      byModel[model] = list(slots).map(busySlot);
    }
  }
  return { global, byModel };
}

export function parseAccessories(value: unknown): Accessory[] {
  return list(object(value).accessories).flatMap(item => {
    const accessory = object(item);
    if (typeof accessory.enabled !== 'boolean') throw new BookingApiError('INVALID_RESPONSE');
    if (!accessory.enabled) return [];
    return [{
      id: text(accessory.id), plate: text(accessory.plate), name: text(accessory.name), enabled: true,
      price: amount(accessory.price), deposit: amount(accessory.deposit),
      weekPrice: amount(accessory.weekPrice), weekendPrice: amount(accessory.weekendPrice),
    }];
  });
}

export async function loadAtvs(signal: AbortSignal) {
  return parseAtvs(await read('getAtvs', signal));
}

export async function loadBusySnapshot(signal: AbortSignal) {
  return parseBusySnapshot(await read('getBusySlots', signal));
}

export async function loadAccessories(signal: AbortSignal) {
  return parseAccessories(await read('getAccessories', signal));
}

function boolean(value: unknown): boolean {
  if (typeof value !== 'boolean') throw new BookingApiError('INVALID_RESPONSE');
  return value;
}

function integer(value: unknown, minimum = 0): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < minimum) throw new BookingApiError('INVALID_RESPONSE');
  return value;
}

function iso(value: unknown): string {
  const result = text(value);
  if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{3})?Z$/.test(result)
    || !Number.isFinite(Date.parse(result))) throw new BookingApiError('INVALID_RESPONSE');
  const normalized = new Date(result).toISOString();
  if (normalized !== (/\.\d{3}Z$/.test(result) ? result : result.replace(/Z$/, '.000Z'))) {
    throw new BookingApiError('INVALID_RESPONSE');
  }
  return normalized;
}

function hour(value: unknown): string {
  const result = text(value);
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(result)) throw new BookingApiError('INVALID_RESPONSE');
  return result;
}

export function parseRentalConfig(value: unknown): RentalConfig {
  const data = object(value), calendar = object(data.calendar), advance = object(data.advancePayment);
  if (data.timezone !== 'Europe/Warsaw' || data.currency !== 'PLN' || data.paymentProvider !== 'imoje') {
    throw new BookingApiError('INVALID_RESPONSE');
  }
  const offers: RentalConfig['offers'] = list(data.offers).map(value => {
    const item = object(value);
    if (item.id !== 'three-hours' && item.id !== 'day' && item.id !== 'weekend') throw new BookingApiError('INVALID_RESPONSE');
    if (item.id === 'three-hours' && (item.durationMinutes !== 180 || item.accessoriesAllowed !== false)
      || item.id === 'day' && (item.defaultDurationMinutes !== 1440 || item.billingPeriodMinutes !== 1440
        || item.allowCustomReturn !== true || item.rounding !== 'ceil')
      || item.id === 'weekend' && item.enabled === true && (item.pickupWeekday !== 5 || item.pickupTime !== '09:00'
        || item.returnWeekday !== 7 || item.returnTime !== '20:00')) throw new BookingApiError('INVALID_RESPONSE');
    return { id: item.id, enabled: boolean(item.enabled), accessoriesAllowed: boolean(item.accessoriesAllowed) };
  });
  if (offers.length !== 3 || new Set(offers.map(item => item.id)).size !== 3
    || calendar.minuteStep !== 30) throw new BookingApiError('INVALID_RESPONSE');
  const openingStart = hour(calendar.openingStart), openingEnd = hour(calendar.openingEnd);
  if (openingStart > openingEnd) throw new BookingApiError('INVALID_RESPONSE');
  return {
    timezone: 'Europe/Warsaw', currency: 'PLN', paymentProvider: 'imoje',
    rulesVersion: text(data.rulesVersion), bookingEnabled: boolean(data.bookingEnabled),
    termsVersion: data.termsVersion === null ? null : text(data.termsVersion), offers,
    calendar: {
      openingStart, openingEnd, minuteStep: 30, maxDays: integer(calendar.maxDays, 1),
      minLeadMinutes: integer(calendar.minLeadMinutes), sameDayCutoff: hour(calendar.sameDayCutoff),
      vehicleBufferMinutes: integer(calendar.vehicleBufferMinutes), accessoryBufferMinutes: integer(calendar.accessoryBufferMinutes),
    },
    advancePayment: { percent: amount(advance.percent), roundingMinor: integer(advance.roundingMinor, 1), cappedAtTotal: boolean(advance.cappedAtTotal) },
    holdMinutes: integer(data.holdMinutes, 1), availabilityTtlSeconds: integer(data.availabilityTtlSeconds, 1),
    quoteTtlSeconds: integer(data.quoteTtlSeconds, 1),
  };
}

export function parseAvailability(value: unknown): AvailabilitySnapshot {
  const data = object(value);
  const entries = (value: unknown): AvailabilitySnapshot['vehicles'] => {
    const ids = new Set<string>();
    return list(value).map(value => {
      const item = object(value), id = text(item.id);
      if (ids.has(id) || !['available', 'unavailable', 'unknown'].includes(String(item.status))) throw new BookingApiError('INVALID_RESPONSE');
      ids.add(id);
      const status = item.status;
      if (status !== 'available' && status !== 'unavailable' && status !== 'unknown') throw new BookingApiError('INVALID_RESPONSE');
      return { id, status };
    });
  };
  const checkedAt = iso(data.checkedAt), expiresAt = iso(data.expiresAt);
  if (Date.parse(expiresAt) <= Date.parse(checkedAt)) throw new BookingApiError('INVALID_RESPONSE');
  return { checkedAt, expiresAt, vehicles: entries(data.vehicles), accessories: entries(data.accessories) };
}

function moneyFields(data: Record<string, unknown>) {
  const result = {
    currency: 'PLN' as const, rentalMinor: integer(data.rentalMinor), accessoriesMinor: integer(data.accessoriesMinor),
    totalMinor: integer(data.totalMinor), refundableDepositMinor: integer(data.refundableDepositMinor), payableNowMinor: integer(data.payableNowMinor),
  };
  if (data.currency !== 'PLN' || result.totalMinor !== result.rentalMinor + result.accessoriesMinor
    || result.payableNowMinor > result.totalMinor) throw new BookingApiError('INVALID_RESPONSE');
  return result;
}

export function parseQuote(value: unknown): BookingQuote {
  const data = object(value);
  return { ...moneyFields(data), quoteId: text(data.quoteId), selectionKey: text(data.selectionKey),
    expiresAt: iso(data.expiresAt), rulesVersion: text(data.rulesVersion) };
}

export function parseCredentials(value: unknown): ReservationCredentials {
  const data = object(value), token = text(data.statusToken);
  if (!/^[a-f0-9]{64}$/i.test(token)) throw new BookingApiError('INVALID_RESPONSE');
  return { reservationId: text(data.reservationId), statusToken: token };
}

export function parseReservationPayload(value: unknown): ReservationPayload {
  const data = object(value), customer = object(data.customer);
  const offerId = data.offerId, paymentType = data.paymentType, locale = data.locale;
  if ((offerId !== 'three-hours' && offerId !== 'day' && offerId !== 'weekend')
    || (paymentType !== 'deposit' && paymentType !== 'full')
    || (locale !== 'pl' && locale !== 'en' && locale !== 'de')
    || typeof data.message !== 'string' || data.message.length > 255) throw new BookingApiError('INVALID_RESPONSE');
  const accessoryIds = list(data.accessoryIds).map(text);
  if (new Set(accessoryIds).size !== accessoryIds.length) throw new BookingApiError('INVALID_RESPONSE');
  return {
    offerId, startsAt: iso(data.startsAt), endsAt: iso(data.endsAt), vehicleId: text(data.vehicleId),
    accessoryIds, paymentType, quoteId: text(data.quoteId),
    customer: { name: text(customer.name), email: text(customer.email), phone: text(customer.phone) },
    message: data.message, termsAccepted: boolean(data.termsAccepted), rodoAccepted: boolean(data.rodoAccepted),
    depositAccepted: boolean(data.depositAccepted), termsVersion: text(data.termsVersion),
    marketingConsent: boolean(data.marketingConsent), locale,
  };
}

export function parseReservationStatus(value: unknown): ReservationStatus {
  const data = object(value);
  const redirectUri = data.redirectUri === null ? null : text(data.redirectUri);
  if (redirectUri) {
    let url: URL;
    try { url = new URL(redirectUri); } catch { throw new BookingApiError('INVALID_RESPONSE'); }
    if (url.protocol !== 'https:' || url.username || url.password) throw new BookingApiError('INVALID_RESPONSE');
  }
  if (data.paymentProvider !== 'imoje') throw new BookingApiError('INVALID_RESPONSE');
  return { ...moneyFields(data), reservationId: text(data.reservationId), status: text(data.status),
    paymentProvider: 'imoje', paymentState: text(data.paymentState), holdExpiresAt: iso(data.holdExpiresAt), redirectUri };
}

export async function loadRentalConfig(signal: AbortSignal) {
  return parseRentalConfig(await read('getRentalConfig', signal));
}

export async function loadAvailability(body: {
  offerId: BookingSelection['offer']; startsAt: string; endsAt: string; timezone: 'Europe/Warsaw';
  vehicleIds: string[]; accessoryIds: string[];
}, signal: AbortSignal) {
  return parseAvailability(await read('checkAvailability', signal, body));
}

export async function loadConfiguredAvailability(
  body: Parameters<typeof loadAvailability>[0], config: RentalConfig, signal: AbortSignal,
) {
  const result = await loadAvailability(body, signal);
  if (Date.parse(result.expiresAt) <= Date.now()
    || Date.parse(result.expiresAt) - Date.parse(result.checkedAt) > config.availabilityTtlSeconds * 1000) {
    throw new BookingApiError('AVAILABILITY_UNKNOWN');
  }
  return result;
}

export async function loadQuote(selection: BookingSelection, signal: AbortSignal): Promise<BookingQuote> {
  const { offer, ...rest } = selection;
  return parseQuote(await read('getRentalQuote', signal, { ...rest,
    startsAt: new Date(rest.startsAt).toISOString(), endsAt: new Date(rest.endsAt).toISOString(),
    accessoryIds: [...rest.accessoryIds].sort(), offerId: offer }));
}

export async function createReservation(payload: ReservationPayload, key: string, signal: AbortSignal) {
  const data = await read('createReservation', signal, payload, key);
  return { ...parseReservationStatus(data), ...parseCredentials(data) };
}

export async function loadReservationStatus(credentials: ReservationCredentials, signal: AbortSignal) {
  const result = parseReservationStatus(await read('getReservationStatus', signal, credentials));
  if (result.reservationId !== credentials.reservationId) throw new BookingApiError('INVALID_RESPONSE');
  return result;
}
